import { Upload, TrendingUp, BarChart3, ArrowRight } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useAnalysisContext } from '../../contexts/AnalysisContext';
import { API_URL } from '../../constants/api';

export function Dashboard({
  dashboardData,
  setShowDashboard,
}) {
  const { deviceId, userEmail } = useUser();
  const { setAnalysisResult, setCurrentSwingId } = useAnalysisContext();

  return (
    <div className="py-8 sm:py-12 max-w-4xl mx-auto">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 text-center sm:text-left">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Welcome back{userEmail ? `, ${userEmail.split('@')[0]}` : ''}!
          </h1>
          {dashboardData.improvement && (
            <p className="text-slate-400 mt-1">
              {dashboardData.improvement.trend === 'improving' ? (
                <span className="text-emerald-400">
                  You've improved {dashboardData.improvement.points} points since you started!
                </span>
              ) : dashboardData.improvement.trend === 'stable' ? (
                <span>Your swing is staying consistent. Keep practicing!</span>
              ) : (
                <span>Let's get back on track with today's practice!</span>
              )}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowDashboard(false)}
          className="rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 px-6 py-3 font-bold text-black hover:scale-105 transition flex items-center justify-center gap-2 mx-auto sm:mx-0"
        >
          <Upload className="h-5 w-5" />
          New Analysis
        </button>
      </div>

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xl sm:text-2xl font-bold text-white">{dashboardData.stats?.totalSwings || 0}</p>
          <p className="text-xs text-slate-500">Swings</p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xl sm:text-2xl font-bold text-emerald-400">{dashboardData.stats?.bestScore || '-'}</p>
          <p className="text-xs text-slate-500">Best</p>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
          <p className="text-xl sm:text-2xl font-bold text-green-400">{dashboardData.stats?.avgScore || '-'}</p>
          <p className="text-xs text-slate-500">Avg</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Birdie Progress Summary */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-400" />
            Your Progress
          </h2>
          {dashboardData.progressSummary ? (
            <div className="flex gap-3 sm:gap-4">
              <img src="/logo-bird.png" alt="Birdie" className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex-shrink-0" />
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">{dashboardData.progressSummary}</p>
            </div>
          ) : dashboardData.stats?.totalSwings >= 2 ? (
            <p className="text-slate-400">Loading your progress summary...</p>
          ) : (
            <p className="text-slate-400">Complete 2+ analyses to see your progress summary from Birdie.</p>
          )}
        </div>

        {/* Recent Swings */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-green-400" />
            Recent Swings
          </h2>
          {dashboardData.recentSwings && dashboardData.recentSwings.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.recentSwings.slice(0, 5).map((swing) => (
                <button
                  key={swing.id}
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/swing/${swing.id}?deviceId=${deviceId}`, {
                        credentials: 'include'
                      });
                      const data = await response.json();
                      if (data.analysis_json) {
                        setAnalysisResult(data.analysis_json);
                        setCurrentSwingId(swing.id);
                        setShowDashboard(false);
                      }
                    } catch (error) {
                      console.error('Failed to load swing:', error);
                    }
                  }}
                  className="w-full flex items-center gap-3 sm:gap-4 rounded-xl bg-black/30 border border-white/5 p-3 sm:p-4 hover:border-emerald-500/30 transition text-left"
                >
                  <div className={`text-xl sm:text-2xl font-bold min-w-[40px] ${
                    swing.swing_score >= 80 ? 'text-emerald-400' :
                    swing.swing_score >= 60 ? 'text-green-400' :
                    'text-amber-400'
                  }`}>
                    {swing.swing_score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base truncate">{swing.club}</p>
                    <p className="text-xs sm:text-sm text-slate-500">
                      {new Date(swing.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No swings yet. Upload your first video!</p>
          )}
        </div>
      </div>
    </div>
  );
}
